// Phase 2 of transcript merge v2: resolves the per-field conflicts surfaced
// by `mergeLeadFromTranscript` when a manually-typed value differs from a
// fresh transcript extraction. The modal lets the user pick "Keep mine" or
// "Use transcript" per field, plus bulk shortcuts for both sides.
//
// State is intentionally local — the parent passes the pending merge result
// and gets back a resolutions map on Apply. The parent calls
// `applyConflictResolutions` and commits the lead (this lives in
// `useLeadMergeConflictController`).

import { useEffect, useMemo, useState } from "react";
import type { ConflictResolution, MergeLeadResult } from "@/lib/lead/extraction/mergeLeadFromTranscript";
import { formatLeadFieldValue, humanizeLeadField } from "@/lib/lead/extraction/leadFieldLabels";

export type LeadMergeConflictModalProps = {
  /** When null the modal is unmounted. */
  pending:
    | {
        mergeResult: MergeLeadResult;
        source: "extract" | "reextract";
      }
    | null;
  onResolve: (resolutions: Record<string, ConflictResolution>) => void;
  onCancel: () => void;
};

type Choice = "keep" | "overwrite";

export function LeadMergeConflictModal({ pending, onResolve, onCancel }: LeadMergeConflictModalProps) {
  const conflicts = pending?.mergeResult.conflicts ?? [];
  const fields = useMemo(() => conflicts.map((c) => c.field as string), [conflicts]);
  const [choices, setChoices] = useState<Record<string, Choice>>({});

  useEffect(() => {
    // Reset choices whenever a new conflict set lands so we don't carry
    // stale picks across separate re-extract runs.
    setChoices({});
  }, [pending]);

  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending, onCancel]);

  if (!pending) return null;

  const allChosen = fields.length > 0 && fields.every((f) => choices[f]);
  const sourceLabel = pending.source === "extract" ? "live extraction" : "re-extract";

  const setAll = (choice: Choice) => {
    const next: Record<string, Choice> = {};
    for (const f of fields) next[f] = choice;
    setChoices(next);
  };

  const apply = () => {
    const resolutions: Record<string, ConflictResolution> = {};
    for (const f of fields) {
      resolutions[f] = choices[f] === "overwrite" ? { kind: "overwrite" } : { kind: "keep" };
    }
    onResolve(resolutions);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Resolve transcript merge conflicts"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-3 py-3 sm:items-center sm:px-6 sm:py-6"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-xl rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Resolve transcript merge conflicts
              </h2>
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                {fields.length} field{fields.length === 1 ? "" : "s"} you typed differ from the {sourceLabel}.
                Pick a value for each. Your typed value is preserved until you click Apply.
              </p>
            </div>
            <button
              type="button"
              onClick={onCancel}
              aria-label="Cancel merge"
              className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-base leading-none"
            >
              ✕
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setAll("keep")}
              className="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Keep all mine
            </button>
            <button
              type="button"
              onClick={() => setAll("overwrite")}
              className="rounded border border-amber-400 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-900/40 dark:text-amber-100 dark:hover:bg-amber-900/60"
            >
              Use all from transcript
            </button>
          </div>
        </header>

        <ul className="max-h-[60vh] divide-y divide-gray-200 overflow-y-auto dark:divide-gray-800">
          {conflicts.map((c) => {
            const fieldKey = c.field as string;
            const choice = choices[fieldKey];
            return (
              <li key={fieldKey} className="px-4 py-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {humanizeLeadField(fieldKey)}
                </div>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <ChoiceCard
                    label="Keep mine"
                    value={formatLeadFieldValue(fieldKey, c.currentValue)}
                    selected={choice === "keep"}
                    tone="keep"
                    onClick={() => setChoices((prev) => ({ ...prev, [fieldKey]: "keep" }))}
                  />
                  <ChoiceCard
                    label="Use from transcript"
                    value={formatLeadFieldValue(fieldKey, c.transcriptValue)}
                    selected={choice === "overwrite"}
                    tone="overwrite"
                    onClick={() => setChoices((prev) => ({ ...prev, [fieldKey]: "overwrite" }))}
                  />
                </div>
              </li>
            );
          })}
        </ul>

        <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3 dark:border-gray-800">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!allChosen}
            className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-emerald-500 dark:hover:bg-emerald-400"
          >
            Apply {fields.length} change{fields.length === 1 ? "" : "s"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ChoiceCard({
  label,
  value,
  selected,
  tone,
  onClick,
}: {
  label: string;
  value: string;
  selected: boolean;
  tone: "keep" | "overwrite";
  onClick: () => void;
}) {
  const selectedRing =
    tone === "keep"
      ? "ring-2 ring-emerald-500 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 dark:border-emerald-700"
      : "ring-2 ring-amber-500 border-amber-300 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-700";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={[
        "flex flex-col items-start gap-1 rounded-lg border px-3 py-2 text-left text-xs",
        selected
          ? selectedRing
          : "border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:hover:bg-gray-800",
      ].join(" ")}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <span className="break-words font-mono text-gray-900 dark:text-gray-100">{value}</span>
    </button>
  );
}
